import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Variables_in_Media_Queries() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">
        📐 Advanced CSS: Variables in Media Queries
      </h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS variables can be dynamically overridden inside media queries to
        create responsive layouts, scalable typography, and adaptive UI
        components. This approach keeps your design consistent while adapting to
        different screen sizes.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#d1fae5,#6ee7b7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Define global variables under <code>:root</code>.
          </li>
          <li>
            Override variables inside <code>@media</code> blocks for responsive
            behavior.
          </li>
          <li>
            Variables can control spacing, font sizes, colors, and grid layout.
          </li>
          <li>
            Combine with component-level variables for isolated responsive
            elements.
          </li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">
          📝 Examples of Variables in Media Queries
        </h2>

        {/* 1. Responsive Font Size */}
        <h3>1. Responsive Font Size</h3>
        <CodeCopyBlock
          code={`:root {
  --font-size: 16px;
}
@media (min-width: 768px) {
  :root { --font-size: 18px; }
}
@media (min-width: 1200px) {
  :root { --font-size: 20px; }
}
p {
  font-size: var(--font-size);
}`}
        />
        <p style={{ fontSize: "var(--font-size,16px)", marginBottom: "1rem" }}>
          This paragraph adjusts its font size based on screen width.
        </p>

        {/* 2. Responsive Button Padding */}
        <h3>2. Responsive Button Padding</h3>
        <CodeCopyBlock
          code={`:root {
  --btn-padding: 8px 16px;
}
@media (min-width:768px){
  :root { --btn-padding: 12px 24px; }
}
.button {
  padding: var(--btn-padding);
  border-radius: 8px;
  background: #3b82f6;
  color: white;
}`}
        />
        <button
          style={{
            padding: "var(--btn-padding,8px 16px)",
            borderRadius: "8px",
            background: "#3b82f6",
            color: "white",
            marginBottom: "1rem",
          }}
        >
          Responsive Button
        </button>

        {/* 3. Grid Column Adjustments */}
        <h3>3. Responsive Grid Columns</h3>
        <CodeCopyBlock
          code={`:root {
  --columns: 1;
}
@media(min-width: 600px){
  :root { --columns: 2; }
}
@media(min-width: 1024px){
  :root { --columns: 3; }
}
.grid {
  display: grid;
  grid-template-columns: repeat(var(--columns), 1fr);
  gap: 10px;
}`}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(var(--columns,1),1fr)",
            gap: "10px",
            marginBottom: "1rem",
          }}
        >
          <div
            style={{
              background: "#fbbf24",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            1
          </div>
          <div
            style={{
              background: "#f97316",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            2
          </div>
          <div
            style={{
              background: "#3b82f6",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            3
          </div>
        </div>

        {/* 4. Responsive Card Shadow */}
        <h3>4. Responsive Card Shadow</h3>
        <CodeCopyBlock
          code={`:root {
  --card-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
@media(min-width:1024px){
  :root { --card-shadow: 0 8px 25px rgba(0,0,0,0.2); }
}
.card {
  padding: 20px;
  background: #e0f2fe;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
}`}
        />
        <div
          style={{
            padding: "20px",
            background: "#e0f2fe",
            borderRadius: "12px",
            boxShadow: "var(--card-shadow,0 4px 12px rgba(0,0,0,0.1))",
            marginBottom: "1rem",
          }}
        >
          Responsive Card Shadow
        </div>

        {/* 5. Responsive Theme Colors */}
        <h3>5. Theme Color Overrides in Media Queries</h3>
        <CodeCopyBlock
          code={`:root {
  --primary-color: #3b82f6;
}
@media(min-width:768px){
  :root { --primary-color: #9333ea; }
}
.button {
  background: var(--primary-color);
  color: white;
  padding: 10px 20px;
}`}
        />
        <button
          style={{
            background: "var(--primary-color,#3b82f6)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            marginBottom: "2rem",
          }}
        >
          Responsive Color Button
        </button>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#d1fae5,#6ee7b7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine media query variables with JS for fully dynamic responsive
            themes.
          </li>
          <li>
            Use variables to control grid layouts, font sizes, and spacing at
            breakpoints.
          </li>
          <li>
            Experiment with component-level overrides inside media queries.
          </li>
          <li>
            Create responsive interactive buttons, cards, and navigation bars.
          </li>
        </ul>
      </div>
    </div>
  );
}
